// Monthly billing workbook for Ferrari and Executive — both use the same
// "relaxed" billing model (hourly + per-mile, owner manually enters
// hours, mileage from owner's home base) and both invoice as a single
// monthly batch instead of per-job.
//
// Layout (one xlsx per broker per calendar month):
//
//   Date | Type | Guard | POD # (HAWB) | Package location | Weight | Hours | Miles | Total
//   ----------------------------------------------------------------------
//   5/2  | G1    | HAWB-12345   | Kay Carolina     | 4.50  | 38    | $228.50  ← merged across packages
//        |       | HAWB-12346   | Kay Concord      |       |       |          ← part of merge above
//        |       |              | Box truck rental |       |       | $89.50   ← expense row
//        |       |              | I-77 toll        |       |       | $12.00   ← expense row
//   5/4  | G1    | HAWB-12350   | Helzberg Concord | 4.00  | 25    | $185.00
//        |       |              |                  |       | TOTAL | $2,455.00 ← grand total
//
// Per-package rows: when a job has multiple packages, each package gets
// its own row. The Date / Guard / Hours / Miles / Total cells merge
// vertically across the package rows so the totals don't get
// double-counted by anyone reading the sheet.
//
// Expenses: itemized rows directly under their parent job, with the
// expense label in the "Package location" column and the amount in
// "Total." Every expense receipt PHOTO attached to the month's jobs also
// downloads as its own one-page PDF (caption + image) alongside the
// workbook — Ferrari requires the receipts submitted with the bill, and
// individual files attach straight to the email.

import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import { loadAndPrepImage } from "./podPdf";

// Format YYYY-MM-DD as M/D for compact display in the workbook.
function fmtDate(ymd) {
  if (!ymd || typeof ymd !== "string") return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${m}/${d}`;
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return "";
  return Number(n);
}

// Extract the city portion from a Google-formatted address string.
// Typical formats:
//   "1 Stoney Creek Rd, Hilton Head Island, SC 29928, USA"
//   "300 King St, Charleston, SC 29401"
//   "Charleston, SC 29401"
// We split on commas and pick the segment that's most likely the city
// based on the number of parts. Returns "" when nothing reasonable
// is found so the caller can degrade gracefully.
function cityFromAddress(address) {
  if (!address) return "";
  const parts = String(address).split(",").map((s) => s.trim()).filter(Boolean);
  // street, city, state-zip, country  → city is parts[1]
  // street, city, state-zip           → city is parts[1]
  // city, state-zip                   → city is parts[0]
  if (parts.length >= 3) return parts[1];
  if (parts.length === 2) return parts[0];
  return "";
}

// Pick the package location label for the bill row. Format:
//   "Recipient Name (City)" when both are available
//   "Recipient Name" if no parseable city
//   "City" if no recipient name (rare)
//
// `pkg` may have a stopId pointing to a specific stop; if so, use that —
// UNLESS that stop is the airport. On an outbound the package is
// "delivered" to the airport to fly out, so its stopId points at CLT;
// the bill row should still name the CUSTOMER stop (the pickup), not
// print "CLT (Charlotte)". Fall back to the job's first non-airport
// pickup (outbound) or last non-airport delivery (inbound/direct).
function packageLocationFor(job, pkg) {
  const stops = job.stops || [];
  let stop = null;
  if (pkg?.stopId) {
    const byId = stops.find((s) => s.id === pkg.stopId) || null;
    if (byId && byId.type !== "airport") stop = byId;
  }
  if (!stop) {
    const isOutbound = job.jobType === "outbound";
    const realStops = stops.filter((s) => s.type !== "airport");
    stop = isOutbound ? realStops[0] : realStops[realStops.length - 1];
  }
  if (!stop) return "";
  const name = (stop.locationName || "").trim();
  const city = cityFromAddress(stop.address);
  if (name && city) return `${name} (${city})`;
  return name || city || stop.address || "";
}

// "Name (City)" label for a route endpoint — the Executive layout shows
// starting/ending locations instead of package details.
function stopLabelFor(stop) {
  if (!stop) return "";
  if (stop.outOfTown) return "OUT OF TOWN";
  const name = (stop.locationName || "").trim();
  const city = cityFromAddress(stop.address);
  if (name && city) return `${name} (${city})`;
  return name || city || stop.address || "";
}

// Compact job-type code for the workbook. Matches the per-job filename
// convention used elsewhere (DIRECTION_CODE in invoiceWorkbook.js):
//   inbound  → "IB"
//   outbound → "OB"
//   direct   → "DR"
//   anything else → "" so an unknown type doesn't pollute the sheet.
function jobTypeCode(job) {
  const t = String(job?.jobType || job?.type || "").toLowerCase();
  if (t === "inbound") return "IB";
  if (t === "outbound") return "OB";
  if (t === "direct") return "DR";
  return "";
}

// Number of guards billed on the job. The larger of numGuards (intent)
// and guardAssignments.length (reality), with a floor of 1. Renders as
// a bare integer in the workbook — "1", "2", "3" — since the broker
// only cares how many guards to pay for, not slot identifiers.
function guardLabelFor(job) {
  const a = Array.isArray(job.guardAssignments) ? job.guardAssignments : [];
  const total = Math.max(Number(job.numGuards) || 1, a.length, 1);
  return String(total);
}

// Sum the round-trip-aware total of a single job's expenses.
function expenseRowsFor(job) {
  const items = Array.isArray(job.expenseItems) ? job.expenseItems : [];
  return items.map((it) => ({
    label: it.label || "(unlabeled)",
    amount: (Number(it.amount) || 0) * (it.roundTrip ? 2 : 1),
  })).filter((r) => r.amount > 0);
}

/**
 * Build and download an .xlsx for a broker + calendar month.
 *
 *   broker    — string label (e.g. "Ferrari" or "Executive")
 *   yearMonth — "YYYY-MM" (e.g. "2026-05")
 *   jobs      — array of job docs whose scheduledDate falls in that
 *               month and whose brokerName matches. Caller filters; this
 *               function trusts the input set.
 */
export async function downloadMonthlyBrokerWorkbook(broker, yearMonth, jobs) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "VeloceFlow";
  wb.created = new Date();
  const ws = wb.addWorksheet(`${broker} ${yearMonth}`);

  // Executive bills by trip, not by package: their layout is the route
  // endpoints + the money columns — no type/guards/POD/weight. Ferrari
  // keeps the per-package layout.
  const isExec = String(broker || "").toLowerCase().includes("exec");

  // Column widths tuned for the typical content; user can adjust in Sheets.
  ws.columns = isExec
    ? [
        { header: "Date", key: "date", width: 8 },
        { header: "Starting location", key: "from", width: 34 },
        { header: "Ending location", key: "to", width: 34 },
        { header: "Hours", key: "hours", width: 8 },
        { header: "Miles", key: "miles", width: 8 },
        { header: "Total", key: "total", width: 12 },
      ]
    : [
        { header: "Date", key: "date", width: 8 },
        { header: "Type", key: "type", width: 6 },
        { header: "Guards", key: "guard", width: 8 },
        { header: "POD #", key: "pod", width: 16 },
        { header: "Package location", key: "loc", width: 36 },
        { header: "Weight", key: "weight", width: 10 },
        { header: "Hours", key: "hours", width: 8 },
        { header: "Miles", key: "miles", width: 8 },
        { header: "Total", key: "total", width: 12 },
      ];

  // Header styling — bold + thin bottom border so the table is obvious.
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => {
    cell.border = { bottom: { style: "thin" } };
    cell.alignment = { vertical: "middle", horizontal: "left" };
  });

  // Title row above headers — broker + month as a banner.
  ws.spliceRows(1, 0, [
    `${broker} — ${yearMonth}`,
  ]);
  ws.mergeCells(1, 1, 1, ws.columns.length); // banner spans whichever layout is active
  ws.getCell("A1").font = { bold: true, size: 14 };
  ws.getCell("A1").alignment = { horizontal: "center" };

  // Sort jobs by date ascending so the month reads top-to-bottom.
  const sortedJobs = jobs.slice().sort((a, b) =>
    String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
  );

  let grandHours = 0;
  let grandMiles = 0;
  let grandTotal = 0;

  for (const job of sortedJobs) {
    const packages = Array.isArray(job.packages) && job.packages.length > 0
      ? job.packages
      : [{ /* synthetic single-row entry for jobs with no listed packages */ }];

    const breakdown = job.billingBreakdown || {};
    const hours = breakdown.hoursCharged ?? breakdown.rawHours ?? 0;
    const miles = breakdown.miles ?? breakdown.totalMiles ?? 0;
    // breakdown.total ALREADY includes expenses (computeJobBilling sums
    // hourly + mileage + expenses). For the workbook we want the per-job
    // row to show only hourly + mileage so the expense rows below don't
    // double-count when added to the grand total.
    const totalWithExpenses = job.billedAmount ?? breakdown.total ?? 0;
    const expensesIncluded = Number(breakdown.expenses) || 0;
    const baseTotal = Math.max(0, Number(totalWithExpenses) - expensesIncluded);
    grandHours += Number(hours) || 0;
    grandMiles += Number(miles) || 0;
    grandTotal += baseTotal;

    if (isExec) {
      // Executive: one row per trip — route endpoints + the money columns.
      const jobStops = Array.isArray(job.stops) ? job.stops : [];
      ws.addRow({
        date:  fmtDate(job.scheduledDate),
        from:  stopLabelFor(jobStops[0]),
        to:    stopLabelFor(jobStops[jobStops.length - 1]),
        hours: fmtMoney(hours),
        miles: fmtMoney(miles),
        // Base total excludes expenses — expenses get their own rows
        // below so the column sums to the grand total cleanly.
        total: fmtMoney(baseTotal),
      });
    } else {
      // Track the start row so we can merge cells across package rows.
      const startRowIdx = ws.rowCount + 1;

      packages.forEach((pkg, i) => {
        const isFirst = i === 0;
        ws.addRow({
          date:  isFirst ? fmtDate(job.scheduledDate) : "",
          type:  isFirst ? jobTypeCode(job) : "",
          guard: isFirst ? guardLabelFor(job) : "",
          pod:   pkg?.hawbNumber || "",
          loc:   packageLocationFor(job, pkg),
          // Per-package weight — shown on every package row (not merged), so a
          // multi-package job lists each parcel's weight on its own line.
          weight: pkg?.weight || "",
          hours: isFirst ? fmtMoney(hours) : "",
          miles: isFirst ? fmtMoney(miles) : "",
          // Base total excludes expenses — expenses get their own rows
          // below so the column sums to the grand total cleanly.
          total: isFirst ? fmtMoney(baseTotal) : "",
        });
      });

      // Merge the shared columns vertically across the package rows so
      // anyone reading the sheet sees the total once, not duplicated.
      if (packages.length > 1) {
        const endRowIdx = startRowIdx + packages.length - 1;
        // Date, Type, Guard, Hours, Miles, Total — columns A, B, C, G, H, I.
        // (Weight, column F, is per-package and intentionally NOT merged.)
        ["A", "B", "C", "G", "H", "I"].forEach((col) => {
          ws.mergeCells(`${col}${startRowIdx}:${col}${endRowIdx}`);
          ws.getCell(`${col}${startRowIdx}`).alignment = { vertical: "middle" };
        });
      }
    }

    // Expense rows under this job — each on its own row, label in the
    // location column of the active layout, amount in "Total." The other
    // cells stay blank to visually distinguish them from job rows.
    const expenses = expenseRowsFor(job);
    for (const exp of expenses) {
      ws.addRow(isExec
        ? { date: "", from: `Expense: ${exp.label}`, to: "", hours: "", miles: "", total: fmtMoney(exp.amount) }
        : {
            date: "",
            type: "",
            guard: "",
            pod: "",
            loc: `Expense: ${exp.label}`,
            hours: "",
            miles: "",
            total: fmtMoney(exp.amount),
          });
      // Italicize the expense rows for visual hierarchy.
      const row = ws.getRow(ws.rowCount);
      row.font = { italic: true, color: { argb: "FF555555" } };
      grandTotal += exp.amount;
    }
  }

  // Grand total row at the bottom, separated by an empty row so it's
  // visually obvious where the sums live.
  ws.addRow({});
  const totalRow = ws.addRow(isExec
    ? {
        date: "",
        from: "",
        to: "GRAND TOTAL",
        hours: fmtMoney(grandHours),
        miles: fmtMoney(grandMiles),
        total: fmtMoney(grandTotal),
      }
    : {
        date: "",
        type: "",
        guard: "",
        pod: "",
        loc: "GRAND TOTAL",
        hours: fmtMoney(grandHours),
        miles: fmtMoney(grandMiles),
        total: fmtMoney(grandTotal),
      });
  totalRow.font = { bold: true, size: 12 };
  totalRow.eachCell((cell) => {
    cell.border = { top: { style: "double" } };
  });

  // Number formatting — money on the Total column, plain decimals on
  // hours, integer on miles.
  ws.getColumn("total").numFmt = '"$"#,##0.00';
  ws.getColumn("hours").numFmt = "0.00";
  ws.getColumn("miles").numFmt = "0";

  // Trigger the download via a dynamically-created anchor.
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${broker}_${yearMonth}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  // ── Receipt PDFs ────────────────────────────────────────────────────
  // Ferrari requires the receipts submitted WITH the monthly bill, so
  // every expense receipt photo attached to this month's jobs downloads
  // as its own one-page PDF right after the workbook — caption (date ·
  // job # · expense · amount) on top, photo fitted below. Individual
  // files attach straight to the email. A photo that can't be fetched is
  // skipped and counted so the caller can warn; the bill itself already
  // downloaded. (The browser may ask once to allow multiple downloads.)
  const receipts = [];
  for (const job of sortedJobs) {
    const items = Array.isArray(job.expenseItems) ? job.expenseItems : [];
    for (const item of items) {
      if (item.receiptPhotoUrl) receipts.push({ job, item });
    }
  }
  let receiptsIncluded = 0;
  let receiptsFailed = 0;
  let receiptsFailReason = "";
  const safeName = (s) => String(s || "").replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
  for (const { job, item } of receipts) {
    const amount = (Number(item.amount) || 0) * (item.roundTrip ? 2 : 1);
    const jobLabel = job.jobNumber || job.packages?.[0]?.hawbNumber || "";
    const caption = [
      fmtDate(job.scheduledDate),
      jobLabel,
      item.label || "Expense",
      `$${amount.toFixed(2)}`,
    ].filter(Boolean).join(" · ");
    try {
      // Same loader as the POD PDF (fetch + EXIF rotation + border crop) —
      // receipts must behave exactly like the POD photos that are proven
      // to load in production. Returns null on any failure.
      // eslint-disable-next-line no-await-in-loop
      const img = await loadAndPrepImage(item.receiptPhotoUrl);
      if (!img) throw new Error("image failed to load");
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 36;
      doc.setFontSize(12);
      doc.text(caption, margin, margin + 6, { maxWidth: pageW - margin * 2 });
      const imgTop = margin + 30;
      const maxW = pageW - margin * 2;
      const maxH = pageH - imgTop - margin;
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1.5);
      const w = img.width * ratio;
      const h = img.height * ratio;
      doc.addImage(
        img.dataUrl, img.format,
        margin + (maxW - w) / 2, imgTop, w, h
      );
      doc.save(`Receipt_${safeName(job.scheduledDate)}_${safeName(jobLabel)}_${safeName(item.label || "expense")}.pdf`);
      receiptsIncluded += 1;
    } catch (err) {
      console.warn("Couldn't build receipt PDF", item.receiptPhotoUrl, err);
      receiptsFailed += 1;
      // Classify the first failure so the toast can say WHY — the owner
      // isn't going to open the browser console. Probe the URL: an HTTP
      // error means the stored file is gone/blocked; a fetch that works
      // but wouldn't decode means an unreadable format (HEIC phone photo).
      if (!receiptsFailReason) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const probe = await fetch(item.receiptPhotoUrl);
          receiptsFailReason = probe.ok
            ? "the photo file is unreadable (likely a HEIC phone photo — re-attach it as a JPG or screenshot)"
            : `the photo fetch returned HTTP ${probe.status}`;
        } catch (probeErr) {
          receiptsFailReason = "the photo fetch was blocked (network/CORS)";
        }
      }
    }
  }

  return {
    filename: a.download,
    jobsIncluded: sortedJobs.length,
    grandTotal,
    receiptsIncluded,
    receiptsFailed,
    receiptsFailReason,
  };
}
